import React from 'react';
import { FiAlertCircle, FiCloud, FiDownloadCloud, FiDroplet, FiGrid, FiLayers, FiMap, FiTrash } from 'react-icons/fi';

import { LocalizationKey } from '../components/annualReport/portfolioLocalization.js';
import {
  HIGH_RISK_COLOUR,
  LOW_RISK_COLOUR,
  MEDIUM_RISK_COLOUR,
  VERY_HIGH_RISK_COLOUR,
  VERY_LOW_RISK_COLOUR,
} from '../constants.js';
import { RiskDriverCategories } from '../types.js';

export function GetRiskDriverIcon({ categoryId }: { categoryId: RiskDriverCategories }): React.ReactElement | null {
  switch (categoryId) {
    case RiskDriverCategories.EcosystemConditionCategory:
      return <FiLayers />;
    case RiskDriverCategories.EcosystemExtentCategory:
      return <FiGrid />;
    case RiskDriverCategories.FreshwaterConsumptionCategory:
      return <FiDroplet />;
    case RiskDriverCategories.GhgEmissionsCategory:
      return <FiDownloadCloud />;
    case RiskDriverCategories.LandUseChangeCategory:
      return <FiMap />;
    case RiskDriverCategories.NonGhgAirPollutantsCategory:
      return <FiCloud />;
    case RiskDriverCategories.WasteGenerationCategory:
      return <FiTrash />;
    case RiskDriverCategories.WaterPollutionCategory:
      return <FiAlertCircle />;
  }
}

export const labelKeyToColor = new Map<string, string>([
  ['very_low', VERY_LOW_RISK_COLOUR],
  ['low', LOW_RISK_COLOUR],
  ['medium', MEDIUM_RISK_COLOUR],
  ['high', HIGH_RISK_COLOUR],
  ['very_high', VERY_HIGH_RISK_COLOUR],
]);

export const valueToLabelKey = (value: number): LocalizationKey => {
  if (!value) {
    return LocalizationKey.very_low;
  }
  if (value < 20) {
    return LocalizationKey.very_low;
  }
  if (value < 40) {
    return LocalizationKey.low;
  }
  if (value < 60) {
    return LocalizationKey.medium;
  }
  if (value < 80) {
    return LocalizationKey.high;
  }
  return LocalizationKey.very_high;
};

//slug validation for hive preference permalink

export function extractSlug(input: string): string {
  const trimmed = input.trim();
  // if they paste a full URL, grab the last segment
  const match = trimmed.match(/\/([^/]+)\/?$/);
  return match?.[1] ?? trimmed;
}

//Only allowing lowercase letters, numbers, and single hyphens
//(no leading/trailing hyphens, no consecutive hyphens).
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

//Wrap text with **text** in localization and render with this function to bold
export const renderBold = (text: string): JSX.Element[] =>
  text.split(/(\*\*.*?\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
//Wrap text with *text* in localization and render with this function to bold
export const renderItalic = (text: string): JSX.Element[] =>
  text.split(/(\*.*?\*)/g).map((part, i) =>
    part.startsWith('*') && part.endsWith('*') ? (
      <em key={i} className="italic">
        {part.slice(1, -1)}
      </em>
    ) : (
      <span key={i}>{part}</span>
    ),
  );

//for tailwind class name conditional joining
export function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

export function formatAddress(...args: Array<string | undefined>): string {
  return args.filter(Boolean).join(', ');
}

//FOR REPORTING PAGINATION BUT CAN BE USED ELSEWHERE!!!
// Build: [start, start+1, start+2, '…', last]
// where the first trio slides around `page`, and we only show '…' if needed.
// Build a sliding trio of numbers plus ellipsis and last
export function buildPages(page: number, pageCount: number): Array<number | '…'> {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  // 3-number sliding window
  const start = Math.min(Math.max(page - 1, 1), Math.max(pageCount - 2, 1));
  const trio: number[] = [];
  for (let n = start; n < start + 3 && n <= pageCount; n++) {
    trio.push(n);
  }

  const last = pageCount;
  const out: Array<number | '…'> = [...trio];

  const lastNum = out[out.length - 1];
  const lastNumeric = typeof lastNum === 'number' ? lastNum : 0;

  if (lastNumeric < last - 1) {
    out.push('…');
  }

  if (lastNumeric < last) {
    out.push(last);
  }

  return out;
}

export const getNameInitials = (
  firstName?: string | null | undefined,
  lastName?: string | null | undefined,
): string => {
  const firstInitial = firstName?.charAt(0).toUpperCase() ?? '';
  const lastInitial = lastName?.charAt(0).toUpperCase() ?? '';
  return `${firstInitial}${lastInitial}` || 'JD'; // Default initials if both are missing
};

export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        //@ts-expect-error expect error
        r: parseInt(result[1], 16),
        //@ts-expect-error expect error
        g: parseInt(result[2], 16),
        //@ts-expect-error expect error
        b: parseInt(result[3], 16),
      }
    : { r: 255, g: 0, b: 0 }; // Default to red if parse fails
};

export const ordinal = (n: number, opts?: { includeNumber?: boolean }): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = s[(v - 20) % 10] ?? s[v] ?? 'th';
  return opts?.includeNumber === false ? suffix : `${n}${suffix}`;
};

//for csv exports
export const slugifyFilePart = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase();

export const formatDateForFileName = (value: string): string => {
  const date = new Date(value);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
